// lib/leasing/formatFit.ts
//
// THE FORMAT A BRAND MOSTLY OPENS, against the format the brief asks for (owner, 2026-10-12: Krispy
// Kreme "mainly opens kiosks, that is not the format targeted by our sourcing"). The surface & format
// criterion stays the scorer's reading of surfaces; the brand's measured mix of French points of sale
// (scripts/brands/brand-formats.ts) caps it: a brand that rarely opens the format asked for cannot
// score high on it, however well its surfaces fit.

export const BRAND_FORMATS = 'FATER_BRAND_FORMATS_V1';
export const FORMATS = ['STANDARD', 'KIOSK', 'CORNER', 'FLAGSHIP', 'DRIVE', 'MOBILE'] as const;
export type Format = (typeof FORMATS)[number];
export type FormatMix = Record<Format, number>;
const SURFACE_FORMAT_ID = 'surface_format_fit';

/** The pickers' options and the grid's wording: one list. */
export const FORMAT_OPTIONS: Array<{ id: Format; fr: string; en: string }> = [
  { id: 'STANDARD', fr: 'Boutique', en: 'Shop' },
  { id: 'KIOSK', fr: 'Kiosque', en: 'Kiosk' },
  { id: 'CORNER', fr: 'Corner', en: 'Corner' },
  { id: 'FLAGSHIP', fr: 'Flagship', en: 'Flagship' },
  { id: 'DRIVE', fr: 'Drive', en: 'Drive' },
  { id: 'MOBILE', fr: 'Unité mobile', en: 'Mobile unit' },
];
export const formatOptions = (lang: string) => FORMAT_OPTIONS.map((o) => ({ id: o.id, label: lang === 'fr' ? o.fr : o.en }));

const LABEL: Record<'fr' | 'en', Record<Format, string>> = {
  fr: { STANDARD: 'boutiques', KIOSK: 'kiosques', CORNER: 'corners', FLAGSHIP: 'flagships', DRIVE: 'drives', MOBILE: 'unités mobiles' },
  en: { STANDARD: 'shops', KIOSK: 'kiosks', CORNER: 'corners', FLAGSHIP: 'flagships', DRIVE: 'drives', MOBILE: 'mobile units' },
};

/** A flagship is a large shop: a brief asking for shops takes it. */
const covers = (asked: Format[]): Format[] => (asked.includes('STANDARD') ? [...new Set<Format>([...asked, 'FLAGSHIP'])] : asked);

/**
 * 0-1: how far the brand's French network is in the formats asked for. Half of it or more in them
 * fits fully; a tenth or less, not at all. Null when nothing is asked or the mix is unknown.
 */
export function formatFitScore(mix: FormatMix | null, asked: Format[]): number | null {
  if (!mix || !asked.length) return null;
  const share = covers(asked).reduce((s, f) => s + (mix[f] ?? 0), 0);
  return Math.max(0, Math.min(1, (share - 0.1) / 0.4));
}

export function formatReason(mix: FormatMix, asked: Format[], lang: 'fr' | 'en' = 'fr'): string {
  const top = FORMATS.filter((f) => mix[f] >= 0.1).sort((a, b) => mix[b] - mix[a]).map((f) => `${Math.round(mix[f] * 100)} % ${LABEL[lang][f]}`).join(', ');
  const wanted = asked.map((f) => LABEL[lang][f]).join(lang === 'fr' ? ' ou ' : ' or ');
  return lang === 'fr' ? `Format : en France, ${top} ; le brief vise des ${wanted}.` : `Format: in France, ${top}; the brief asks for ${wanted}.`;
}

export const asFormats = (value: unknown): Format[] => (Array.isArray(value) ? value.filter((v): v is Format => (FORMATS as readonly string[]).includes(v as string)) : []);

type FormatsDb = { dataAudit: { findFirst: (args: any) => Promise<{ suggestedFix: unknown } | null> } };

export async function readFormatMix(client: FormatsDb, brandId: string): Promise<FormatMix | null> {
  const row = await client.dataAudit.findFirst({ where: { procedureName: BRAND_FORMATS, entityId: brandId }, orderBy: { createdAt: 'desc' }, select: { suggestedFix: true } });
  const shares = (row?.suggestedFix as { shares?: Partial<FormatMix> } | null)?.shares;
  return shares ? (Object.fromEntries(FORMATS.map((f) => [f, Number(shares[f] ?? 0)])) as FormatMix) : null;
}

/** Caps the surface & format criterion with the brand's measured format mix. Mutates nothing. */
export async function applyFormatFit<T extends { criterionId: string; score: number; maxScore: number; reasoning: string }>(
  client: FormatsDb, results: T[], asked: Format[], brandId: string,
): Promise<T[]> {
  const i = results.findIndex((c) => c.criterionId === SURFACE_FORMAT_ID);
  if (i < 0 || !asked.length) return results;
  const mix = await readFormatMix(client, brandId);
  const fit = formatFitScore(mix, asked);
  if (fit === null || !mix) return results;
  const current = results[i]!;
  const next = [...results];
  next[i] = { ...current, score: Math.min(current.score, Math.round(fit * current.maxScore)), reasoning: `${current.reasoning} ${formatReason(mix, asked)}`.trim() };
  return next;
}
